import React, { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box, Paper, IconButton, Button, TextField, Select, MenuItem, Typography } from '@material-ui/core';

// ✅ MUI v4 icons
import AddIcon from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

import { getRoleHierarchy, getRoleIcon, getRoleColor } from '../utils';

export default function TreeNode({ node, onAddChild, onMoveNode, level = 0 }) {
  const [showInput, setShowInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const availableRoles = getRoleHierarchy(node.role) || [];

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: node.id,
    data: { type: 'agent', node },
  });

  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: `drop-${node.id}`,
    data: { type: 'container', accepts: ['agent'] },
  });

  const handleAdd = () => {
    if (newName.trim() && selectedRole) {
      onAddChild(node.id, newName.trim(), selectedRole);
      setNewName('');
      setSelectedRole('');
      setShowInput(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleAdd();
    } else if (e.key === 'Escape') {
      setShowInput(false);
      setNewName('');
      setSelectedRole('');
    }
  };

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  const getTextSize = () => {
    if (level === 0) return 'h6';
    if (level === 1) return 'subtitle1';
    return 'body2';
  };

  return (
    <Box style={{ position: 'relative' }}>
      {/* Connection line for non-root */}
      {level > 0 && (
        <Box
          style={{
            position: 'absolute',
            left: -24,
            top: 24,
            height: 1,
            width: 24,
            backgroundColor: '#ccc',
          }}
        />
      )}

      <Box style={{ marginLeft: level > 0 ? 32 : 0, marginTop: 12 }}>
        <Box
          ref={setDropRef}
          style={{
            position: 'relative',
            border: isOver ? '2px solid #64b5f6' : 'none',
            borderRadius: 12,
          }}>
          <Paper
            ref={setDragRef}
            {...attributes}
            {...listeners}
            style={{
              ...style,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              borderRadius: 12,
              background: getRoleColor(node.role, level),
              color: '#fff',
              cursor: isDragging ? 'grabbing' : 'grab',
              boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
            }}>
            {/* Drag handle */}
            <DragIndicatorIcon fontSize="small" style={{ opacity: 0.7, color: 'white' }} />

            {/* Expand/Collapse */}
            {(node.children?.length ?? 0) > 0 && (
              <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} style={{ color: 'white' }}>
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            )}

            {/* Node Icon */}
            <Box
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                padding: 6,
                fontSize: 18,
              }}>
              {getRoleIcon(node.role)}
            </Box>

            {/* Name & Role */}
            <Box style={{ flex: 1 }}>
              <Typography variant={getTextSize()} style={{ color: '#fff' }}>
                {node.name}
              </Typography>
              <Typography variant="caption" style={{ opacity: 0.8, color: '#fff' }}>
                {node.role}
              </Typography>
            </Box>

            {/* Add Child Buttons */}
            <Box style={{ display: 'flex', gap: 4 }}>
              {availableRoles.map(role => (
                <IconButton
                  key={role}
                  size="small"
                  onClick={() => {
                    setSelectedRole(role);
                    setShowInput(true);
                  }}
                  title={`Add ${role}`}
                  style={{ color: 'white' }}>
                  {role === 'SP' ? <PersonAddIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                </IconButton>
              ))}
            </Box>
          </Paper>

          {/* Input for adding new child */}
          {showInput && (
            <Box style={{ marginTop: 12, marginLeft: 40 }}>
              <Paper style={{ padding: 16, borderRadius: 8 }}>
                <Box style={{ display: 'flex', gap: 8 }}>
                  <Select value={selectedRole} onChange={e => setSelectedRole(e.target.value)} style={{ minWidth: 120 }}>
                    <MenuItem value="">Select Role</MenuItem>
                    {availableRoles.map(role => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={`Enter ${selectedRole || 'team member'} name`}
                    autoFocus
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Box>
                <Box
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                  }}>
                  <Button
                    onClick={() => {
                      setShowInput(false);
                      setNewName('');
                      setSelectedRole('');
                    }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdd}
                    disabled={!newName.trim() || !selectedRole}>
                    Add {selectedRole}
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Children */}
        {isExpanded && (node.children?.length ?? 0) > 0 && (
          <Box style={{ position: 'relative', marginTop: 16 }}>
            {/* Vertical line */}
            <Box
              style={{
                position: 'absolute',
                left: 24,
                top: 0,
                bottom: 0,
                width: 1,
                backgroundColor: '#ccc',
              }}
            />
            <Box>
              {node.children.map(child => (
                <Box key={child.id} style={{ position: 'relative', marginTop: 8 }}>
                  {/* Horizontal line */}
                  <Box
                    style={{
                      position: 'absolute',
                      left: 24,
                      top: 24,
                      height: 1,
                      width: 24,
                      backgroundColor: '#ccc',
                    }}
                  />
                  <TreeNode node={child} onAddChild={onAddChild} onMoveNode={onMoveNode} level={level + 1} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
